// dependencies
import * as Sentry from "@sentry/react";

// utils
import Print from "../utils/Print";

class Socket {
	/*
        This is a wrapper around WebSocket that will automatically reconnect if
        the connection is lost. It will also use the current protocol and
        generate an appropriate url scheme.

        @param {string} url - The url endpoint of the websocket
        @param {Object} opts - Options to pass the wrapper:
            : timeout - How long until it should try and reconnect in seconds
            : reconnect - Maximum number of times to try and reconnect
    */

	constructor(url, opts = {}) {
		this.opts = opts;
		this.url = `${this.getScheme()}${url}`;

		this.socket = null;

		this.totalReconnects = 0;
		this.retryTimeout = opts.timeout || 5000;
		this.maxReconnects = opts.reconnect || Infinity;

		this.listeners = {
			onopen: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
			onmessage: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
			onerror: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
			onreconnect: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
			onclose: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
		};

		this.open();
	}

	getScheme() {
		let newURI = "";
		const { location } = window;

		newURI += location.protocol === "https:" ? "wss:" : "ws:";
		newURI += `//${location.host}`;

		return newURI;
	}

	reconnect(e) {
		if (this.totalReconnects < this.maxReconnects) {
			setTimeout(() => {
				this.listeners.onreconnect(e);
				this.totalReconnects += 1;
				this.open();
			}, this.retryTimeout);
		} else {
			Print("warn", `Unable to reconnect to ${this.url}`);
		}
	}

	on(listener, callback) {
		switch (listener) {
			case "open":
				this.listeners.onopen = callback;
				break;

			case "close":
				this.listeners.onclose = callback;
				break;

			case "message":
				this.listeners.onmessage = callback;
				break;

			case "error":
				this.listeners.onerror = callback;
				break;

			case "reconnect":
				this.listeners.onreconnect = callback;
				break;

			default:
				Print(`No such listener: ${listener}`);
		}
	}

	open() {
		this.socket = new WebSocket(this.url);

		this.socket.onopen = () => {
			this.totalReconnects = 0;
			this.listeners.onopen();
		};

		this.socket.onmessage = (message) => {
			const payload = {
				data: JSON.parse(message.data),
				original: message,
			};

			this.listeners.onmessage(payload);
		};

		this.socket.onclose = (e) => {
			if (e.code !== 1000 && e.code !== 1005) {
				this.reconnect(e);
			}

			this.listeners.onclose();
		};

		this.socket.onerror = (e) => {
			if (e && e.code === "ECONNREFUSED") {
				this.reconnect(e);
			} else {
				this.listeners.onerror(e);
			}
		};
	}

	send(data) {
		if (this.socket) {
			this.socket.send(data);
		}
	}

	close() {
		if (this.socket) {
			this.socket.close();
		}
	}
}

export default function createSocket(...args) {
	return new Socket(...args);
}
